import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie, X, Settings } from 'lucide-react';

export default function CookieConsent() {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  // Don't show cookie banner on admin pages or developer pages (internal/authenticated)
  const isInternalPage = location.pathname.startsWith('/admin') || location.pathname.startsWith('/developer');

  useEffect(() => {
    if (isInternalPage) {
      setShowBanner(false);
      return;
    }
    // Check if user already accepted cookies
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a small delay
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isInternalPage]);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(selected));
    setShowBanner(false);
  };

  const declineAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-24 left-0 right-0 z-50 p-2 sm:p-4 animate-in slide-in-from-bottom duration-500" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto bg-[#0a0a0a] border border-white/10 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden">
        {!showSettings ? (
          // Main Banner
          <div className="p-3 sm:p-6">
            <div className="flex items-start gap-2 sm:gap-4">
              <div className="p-2 sm:p-3 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg sm:rounded-xl flex-shrink-0">
                <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-lg font-semibold text-white mb-1 sm:mb-2">
                  Folosim cookie-uri
                </h3>
                <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">
                  <span className="hidden sm:inline">Utilizăm cookie-uri pentru a-ți oferi cea mai bună experiență. Cookie-urile ne ajută să înțelegem cum folosești site-ul și să îl îmbunătățim continuu. </span>
                  <span className="sm:hidden">Folosim cookie-uri pentru a-ți oferi cea mai bună experiență. </span>
                  <Link to="/cookies" className="text-blue-400 hover:text-blue-300">
                    Află mai multe
                  </Link>
                </p>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <Button
                    onClick={acceptAll}
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-3 sm:px-6 text-xs sm:text-sm h-8 sm:h-10"
                    data-testid="cookie-accept-all-btn"
                  >
                    Acceptă toate
                  </Button>
                  <Button
                    onClick={declineAll}
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/5 text-xs sm:text-sm h-8 sm:h-10"
                    data-testid="cookie-decline-btn"
                  >
                    Doar esențiale
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4"
                    data-testid="cookie-customize-btn"
                  >
                    <Settings className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Personalizează</span>
                    <span className="sm:hidden">Setări</span>
                  </Button>
                </div>
              </div>
              <button
                onClick={declineAll}
                className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
                aria-label="Închide"
                data-testid="cookie-close-btn"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        ) : (
          // Settings Panel
          <div className="p-3 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-white">Setări Cookie-uri</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-slate-500 hover:text-white transition-colors"
                aria-label="Înapoi"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            
            <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg sm:rounded-xl">
                <div className="flex-1 min-w-0 pr-2">
                  <h4 className="font-medium text-white text-sm sm:text-base">Cookie-uri esențiale</h4>
                  <p className="text-xs sm:text-sm text-slate-400">Necesare pentru funcționarea site-ului</p>
                </div>
                <div className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] sm:text-sm rounded-full flex-shrink-0 whitespace-nowrap">
                  Active
                </div>
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg sm:rounded-xl">
                <div className="flex-1 min-w-0 pr-2">
                  <h4 className="font-medium text-white text-sm sm:text-base">Cookie-uri analitice</h4>
                  <p className="text-xs sm:text-sm text-slate-400">Ne ajută să înțelegem cum folosești site-ul</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg sm:rounded-xl">
                <div className="flex-1 min-w-0 pr-2">
                  <h4 className="font-medium text-white text-sm sm:text-base">Cookie-uri de marketing</h4>
                  <p className="text-xs sm:text-sm text-slate-400">Pentru reclame personalizate</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={acceptSelected}
                size="sm"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white flex-1 text-xs sm:text-sm h-9 sm:h-10"
                data-testid="cookie-save-preferences-btn"
              >
                Salvează preferințele
              </Button>
              <Button
                onClick={acceptAll}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/5 text-xs sm:text-sm h-9 sm:h-10"
              >
                Acceptă toate
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
